import React, { useState, useEffect, useRef } from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';
import PropTypes from 'prop-types';
import { useSnackbar } from 'notistack';
import useDialog from '../../../../web-app-components/hooks/useDialog';
import ButtonSlim from '../../../../web-app-components/components/ButtonSlim';
import { Formik, useFormikContext } from 'formik';
import makeStyles from '@material-ui/core/styles/makeStyles';
import CircularProgress from '@material-ui/core/CircularProgress';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import Box from '@material-ui/core/Box';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';

import objectsClient from '../../../../clients/objects';

import { toForm, fromForm } from '../../utils/formData';
import validationSchema from '../../utils/validationSchema';
import prepareFiles from '../../utils/prepareFiles';

import Form from '../../components/Form';

import ConfirmRemoveDialog from './components/ConfirmRemoveDialog';
import ObjectSpendCodesSidebar from './components/ObjectSpendCodesSidebar';

// Component to handle locale changes (only when user changes the dropdown, not when we set locale from load)
const LocaleChangeHandler = ({ currentLocale, onLocaleChange }) => {
  const { values } = useFormikContext();
  const prevFormLocaleRef = useRef(values.locale);
  const onLocaleChangeRef = useRef(onLocaleChange);
  onLocaleChangeRef.current = onLocaleChange;

  useEffect(() => {
    const formLocale = values.locale;
    const formLocaleChanged = formLocale !== prevFormLocaleRef.current;
    prevFormLocaleRef.current = formLocale;

    // Only react when the form's locale field actually changed (user selected a new locale)
    if (formLocaleChanged && formLocale !== currentLocale) {
      onLocaleChangeRef.current(formLocale);
    }
  }, [values.locale, currentLocale]);

  return null;
};

const useStyles = makeStyles((theme) => ({
  header: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
    width: '100%',
    marginBottom: theme.spacing(2),
  },
  headerLeft: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    width: 'fit-content',
  },
  title: {},
  navButtons: {
    display: 'flex',
    gap: theme.spacing(1),
  },
  container: {
    marginTop: theme.spacing(2),
  },
  fw: {
    flex: 1,
  },
  sidebar: {
    position: 'sticky',
    top: theme.spacing(2),
    alignSelf: 'flex-start',
    [theme.breakpoints.down('md')]: {
      position: 'relative',
      top: 0,
    },
  },
}));

const ExistingObject = ({ id }) => {
  const classes = useStyles();

  const history = useHistory();
  const { path } = useRouteMatch();

  const { enqueueSnackbar } = useSnackbar();

  const confirmRemoveDialog = useDialog();

  const [data, setData] = useState();
  const [currentLocale, setCurrentLocale] = useState('lt-LT');
  const [nextObjectId, setNextObjectId] = useState(null);
  const [previousObjectId, setPreviousObjectId] = useState(null);
  const formikRef = useRef(null);
  const prevLocaleRef = useRef('lt-LT');

  const createMissingLocale = async (locale, sourceData) => {
    if (!sourceData) return null;
    const payload = fromForm({
      ...toForm(sourceData),
      locale,
    });
    return objectsClient.createLocalization(id, payload, locale);
  };

  const loadDataForLocale = async (
    locale,
    updateForm = true,
    attemptedCreate = false,
  ) => {
    try {
      // Fetch object for specified locale
      const localeData = await objectsClient.findOne(id, locale);
      setData(localeData);
      setCurrentLocale(locale);
      prevLocaleRef.current = locale;
      // Update form values if formik is available and updateForm is true
      if (updateForm && formikRef.current) {
        formikRef.current.setValues(
          toForm({
            ...localeData,
            locale: locale,
          }),
        );
      }
    } catch (error) {
      // If locale doesn't exist, try creating it from default locale
      if (locale !== 'lt-LT') {
        try {
          const ltData = data || (await objectsClient.findOne(id, 'lt-LT'));
          if (error?.response?.status === 404 && !attemptedCreate && ltData) {
            await createMissingLocale(locale, ltData);
            return loadDataForLocale(locale, updateForm, true);
          }

          setData(ltData);
          setCurrentLocale('lt-LT');
          prevLocaleRef.current = 'lt-LT';
          if (updateForm && formikRef.current) {
            formikRef.current.setValues(
              toForm({
                ...ltData,
                locale: 'lt-LT',
              }),
            );
          }
        } catch (ltError) {
          console.warn('Failed to load or create locale', ltError);
          enqueueSnackbar(
            'Nepavyko gauti objekto. Bandykite atidaryti iš naujo.',
            { variant: 'error' },
          );
        }
      } else {
        enqueueSnackbar(
          'Nepavyko gauti objekto. Bandykite atidaryti iš naujo.',
          { variant: 'error' },
        );
      }
    }
  };

  useEffect(() => {
    loadDataForLocale('lt-LT', false);
  }, [id, enqueueSnackbar]);

  // Fetch list to resolve next/previous object (same order as list: createdAt desc)
  useEffect(() => {
    if (!id) {
      setNextObjectId(null);
      setPreviousObjectId(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const query = {
          sort: 'createdAt:desc',
        };
        const res = await objectsClient.find(query);
        const list = Array.isArray(res) ? res : (res?.data ?? []);
        const idx = list.findIndex((o) => o.id === id);
        const next = idx >= 0 && idx < list.length - 1 ? list[idx + 1] : null;
        const previous = idx > 0 ? list[idx - 1] : null;
        if (!cancelled) {
          setNextObjectId(next?.id ?? null);
          setPreviousObjectId(previous?.id ?? null);
        }
      } catch {
        if (!cancelled) {
          setNextObjectId(null);
          setPreviousObjectId(null);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  const handlePreviousObjectClick = () => {
    if (previousObjectId) history.push(`/objects/${previousObjectId}`);
  };

  const handleNextObjectClick = () => {
    if (nextObjectId) history.push(`/objects/${nextObjectId}`);
  };

  const handleRemoveClick = async () => confirmRemoveDialog.setOpen(true);

  const handleRemoveConfirm = async () => {
    try {
      await objectsClient.remove(id);
      enqueueSnackbar('Objektas pašalintas', { variant: 'success' });
      history.push(`${path.replace('/:id', '')}`);
    } catch (error) {
      enqueueSnackbar('Nepavyko pašalinti objekto. Bandykite dar kartą.', {
        variant: 'error',
      });
    }
  };

  const handleSubmit = async (values) => {
    try {
      const { Pictures, AudioRecordings, ...newValues } = values;
      if (Array.isArray(Pictures))
        newValues.Pictures = await prepareFiles(values.Pictures);
      if (Array.isArray(AudioRecordings))
        newValues.AudioRecordings = await prepareFiles(values.AudioRecordings);
      const formData = fromForm(newValues);

      const { locale, ...updateData } = formData;

      try {
        await objectsClient.update(id, updateData, locale);
      } catch (error) {
        // If update fails and locale is not default, try creating localization
        if (locale !== 'lt-LT') {
          try {
            await objectsClient.createLocalization(id, updateData, locale);
          } catch (createError) {
            throw createError;
          }
        } else {
          throw error;
        }
      }

      enqueueSnackbar('Objektas atnaujintas', { variant: 'success' });

      // Reload data for the updated locale
      await loadDataForLocale(locale);
    } catch (error) {
      console.error('Error updating object:', error);
      enqueueSnackbar('Nepavyko atnaujinti objekto. Bandykite dar kartą.', {
        variant: 'error',
      });
    }
  };

  return data ? (
    <div>
      <Box className={classes.header}>
        <div className={classes.fw}>
          <Typography className={classes.title} variant="h5">
            Objektas "{data.Title}"
          </Typography>
          <div className={classes.navButtons}>
            <ButtonSlim
              startIcon={<ArrowBackIcon />}
              onClick={handlePreviousObjectClick}
              disabled={!previousObjectId}
            >
              Ankstesnis objektas
            </ButtonSlim>
            <ButtonSlim
              endIcon={<ArrowForwardIcon />}
              onClick={handleNextObjectClick}
              disabled={!nextObjectId}
            >
              Kitas objektas
            </ButtonSlim>
          </div>
        </div>
        <ButtonSlim onClick={handleRemoveClick}>Pašalinti objektą</ButtonSlim>
      </Box>
      <Grid container spacing={3} className={classes.container}>
        <Grid item xs={12} md={8}>
          <Formik
            key={`object-form-${id}-${currentLocale}`}
            innerRef={formikRef}
            initialValues={toForm({
              ...data,
              locale: currentLocale,
            })}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
            enableReinitialize
          >
            {(props) => (
              <>
                <LocaleChangeHandler
                  currentLocale={currentLocale}
                  onLocaleChange={(locale) => {
                    if (data) {
                      loadDataForLocale(locale, true);
                    }
                  }}
                />
                <Form {...props} objectId={id} />
              </>
            )}
          </Formik>
        </Grid>
        <Grid item xs={12} md={4}>
          <div className={classes.sidebar}>
            <ObjectSpendCodesSidebar objectId={id} />
          </div>
        </Grid>
      </Grid>
      <ConfirmRemoveDialog
        {...confirmRemoveDialog}
        onConfirm={handleRemoveConfirm}
      />
    </div>
  ) : (
    <CircularProgress />
  );
};

ExistingObject.propTypes = {
  id: PropTypes.string.isRequired,
};

export default ExistingObject;
